<template>
  <v-container
    id="stream"
    class="fill-height justify-center"
    tag="section"
  >
    <v-fade-transition>
      <div class="status">
        <div v-if="streamStatus === StreamStatus.INIT" class="fade-element">
          {{ $t('stream.checking-your-credentials') }} </div> 
        <div v-if="streamStatus === StreamStatus.STARTING" class="fade-element">
          {{ $t('stream.launch-wait') }}
        </div>
        <div v-if="isDebugMode" class="fade-element">
          Running in debug mode. Please launch Unity on your local machine.
        </div>
        <div v-if="error" class="fade-element">
          <span class="errorMessage">{{ error }}</span>
        </div>
      </div>
    </v-fade-transition>
    
    <v-card class="stream">
      <v-card-title>
        {{ $t('stream.web-stream') }}<v-spacer />
        <v-btn v-if="streamStatus === StreamStatus.RUNNING" id="button_stop" color="red" @click="showCloseConfirmDialog">
          {{ $t('stream.stop') }}
        </v-btn>
        <v-btn v-if="streamStatus !== StreamStatus.INIT" id="button_maximize" color="secondary" @click="maximizeIFrame">
          {{ $t('stream.full-screen') }}
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div v-if="!error && !isDebugMode && streamStatus !== StreamStatus.INIT">
          <iframe id="vagonFrame"
                  :src="streamingURL"
                  style="height:600px; width: 100%; border: none;"
                  frameborder="0"
                  allow="microphone  *; clipboard-read *; clipboard-write *; encrypted-media *;"
                  allowfullscreen
          />
          <br>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showCloseConfirm" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('stream.close') }}<br></v-card-title>
        <v-card-text>{{ $t('stop-playing-bodyswaps') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showCloseConfirm = false">
            {{ $t('global.no') }}
          </v-btn>
          <v-btn color="red" @click="confirmCloseDialog">
            {{ $t('global.yes') }}
          </v-btn>
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import api from '@/helpers/api.js'

  class StreamStatus {
    static INIT = 'init'
    static STARTING = 'starting'
    static RUNNING = 'running'
    static CLOSING = 'closing'
    static STOPPED = 'stopped'
  }

  export default {
    name: 'Stream',

    data: () => ({
      error: undefined,
      streamStatus: StreamStatus.INIT,
      showCloseConfirm: false,
      webSocket: undefined,
      redirect: '',
      launchParameters: {
        shortCode: '123456',
      },
      StreamStatus,
    }),

    computed: {
      streamingURL () {
        const queryParams = '?launchFlags=' + this.formatLaunchParams(this.launchParameters) + '&newSession=true'
        
        const customStreamLink = this.$store.getters.currentUserProfile?.account?.streamingLink ?? undefined
        // check if the account data has a customised link and it's not an empty string
        if (customStreamLink && customStreamLink.trim() !== '') {
          console.log('Using custom stream url: ', customStreamLink)
          return customStreamLink + queryParams
        }
        console.log('Using stream url: ', process.env.VUE_APP_VAGON_STREAM_URL + queryParams)
        return process.env.VUE_APP_VAGON_STREAM_URL + queryParams
      },

      isDebugMode () {
        return this.$route.query.debug ?? false
      },
    },

    async mounted () {
      try {
        // store some query params
        this.redirect = this.$route.query.redirect ?? undefined

        if (this.$route.query.lang) {
          this.lang = this.$route.query.lang
          this.$i18n.locale = this.$route.query.lang
        }

        console.log('Initializing stream...')

        // try to get a code otherwise show error message
        const res = await this.requestCode()
        if (res.error) {
          this.error = res.errorMessage
          return
        }

        this.launchParameters.shortCode = res.shortCode
        console.log('Session code received ')
        
        // init Vagon stream
        await this.initVagon()
      } catch (ex) {
        console.error(ex)
        if (ex.stack) console.error(ex.stack)
        this.error = ex.message
      }
    },

    beforeDestroy () {
      // this.streamClosing = true
      this.streamStatus = StreamStatus.CLOSING
      if (this.webSocket !== undefined) {
        this.webSocket.onmessage = null
        this.webSocket.close()
        this.webSocket = null
        console.log('Local websocket closed.')
      }
    },

    methods: {
      async initVagon () {
        this.streamStatus = StreamStatus.STARTING
        // We can run this in debug mode for local testing
        if (this.isDebugMode) {
          console.log('Using local Unity server...')
          this.connectToLocalServer()
        } else {
          if (!window.Vagon) {
            console.log('Loading Vagon...')
            const script = document.createElement('script')
            script.src = 'https://app.vagon.io/vagonsdk.js'
            script.async = true
            document.head.appendChild(script)
          }
          else {
            console.log('Vagon already loaded.')
          }
          this.vagonRegisterMessage()
        }
      },

      formatLaunchParams (obj) {
        // given an object convert it to a query string
        return Object.keys(obj)
          .map(key => `-${key}%20${obj[key]}`)
          .join('&')
      },

      redirectToUrl (redirectUrl) {
        window.location.replace(redirectUrl)
      },

      isJsonString (str) {
        try {
          JSON.parse(str)
          return true
        } catch (e) {
          return false
        }
      },

      connectToLocalServer () {
        // Open up a websocket to port 7788
        this.webSocket = new WebSocket('ws://localhost:7788')
        this.webSocket.onopen = () => {
          console.log('Connected to local websocket')
        }
        // add a listener to receive messages from the server
        this.webSocket.onmessage = (event) => {
          // console.log('Received message from local websocket: ', event.data)
          console.log('event.data type = ', typeof event.data)
          if (event.data instanceof Blob) {
            console.log('blob')
            const reader = new FileReader()
            reader.onload = () => {
              try {
                console.log(reader.result)
                this.onAppReceiveData(reader.result)
              } catch (ex) {
                console.log(ex)
                if (ex.stack) console.error(ex.stack)
              }
            }
            reader.onerror = function () {
              console.error('FileReader error:', reader.error)
            }
            reader.readAsText(event.data)
          } else {
            console.log('data= ', event.data)
            this.onAppReceiveData(event.data)
          }
        }
      },

      isWebsocketConnected () {
        if (this.isDebugMode) {
          return this.webSocket.readyState === WebSocket.OPEN
        }
        if (window.Vagon) {
          return window.Vagon.isConnected
        } else {
          return false
        }
      },

      async vagonRegisterMessage () {
        let tries = 0
        while (!window.Vagon) {
          tries++
          console.log('Waiting for Vagon window to be ready (' + tries + ')...')
          await new Promise((resolve) => setTimeout(resolve, 100))
        }
        window.Vagon.onApplicationMessage(this.onVagonMessageReceived)
        window.Vagon.onConnected(() => {
          console.log('Vagon connected')
          this.streamStatus = StreamStatus.RUNNING
        })
        console.log('Registered Vagon message handler.')
      },

      sendMessage (msg) {
        // console.log('Message (JSON.stringify) sent: ' + JSON.stringify(msg))
        if (this.isDebugMode) {
          this.webSocket.send(JSON.stringify(msg))
          return
        }
        if (this.isWebsocketConnected()) {
          window.Vagon.sendApplicationMessage(JSON.stringify(msg))
        } else {
          console.error('Impossible to send message, Vagon not connected.')
        }
      },

      confirmCloseDialog () {
        this.stopStream()
        this.showCloseConfirm = false
      },

      showCloseConfirmDialog () {
        this.showCloseConfirm = true
      },

      stopStream () {
        this.streamStatus = StreamStatus.CLOSING
        console.log('Stopping stream...')
        if (window.Vagon) {
          window.Vagon.shutdown()
          this.minimizeIFrame()
        }
        this.redirectOnComplete()
      },

      maximizeIFrame () {
        const iFrame = document.getElementById('vagonFrame')
        // Request fullscreen
        if (iFrame.requestFullscreen) {
          iFrame.requestFullscreen()
        } else if (iFrame.mozRequestFullScreen) {
          iFrame.mozRequestFullScreen() // Firefox
        } else if (iFrame.webkitRequestFullscreen) {
          iFrame.webkitRequestFullscreen() // Chrome, Safari, and Opera
        }
      },

      minimizeIFrame () {
        // Exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen() // Firefox
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen() // Chrome, Safari, and Opera
        }
      },

      
      onVagonMessageReceived (evt) {
        if (this.streamStatus !== StreamStatus.CLOSING) {
          this.onAppReceiveData(evt.message)
        }
        else {
          console.log('App has shut down - message discarded! ' + evt.message)
        }
      },
      
      // This processes any data coming from the remote app (emulated or otherwise)
      async onAppReceiveData (stringData) {
        let data = JSON.parse('{}')
        if (typeof stringData === 'string') {
          if (stringData === '##vagonDisonnect') {
            console.log('Call received: ##vagonDisonnect')
            return
          }
          if (this.isJsonString(stringData)) {
            data = JSON.parse(stringData)
          } else {
            console.log('Invalid JSON string ignoring: ' + stringData)
            return
          }
        } else {
          data = stringData
        }
        console.log('Message received from app... ' + data.call)

        switch (data.call) {
          case 'report.error':
          {
            switch (data.value) {
              case 'stale':
                this.error = this.$t('stream.connection-error')
                break
              default:
                this.error = this.$t('stream.error-type-message') + data.value
                break
            }
            this.stopStream()
            this.streamStatus = StreamStatus.CLOSING
            break
          }
          case 'codeGenerate.request':
          {
            if (!this.launchParameters.shortCode)
            {
              this.stopStream()
              console.error('Could not return a codeGenerate')
            }
            else {
              console.log('returning shortcode')
              this.streamStatus = StreamStatus.RUNNING
              this.sendMessage({ call: data.returnCall, value: this.launchParameters.shortCode })
            }
            break
          }
          case 'application.isShuttingDown':
          {
            this.stopStream()
            this.streamStatus = StreamStatus.CLOSING
            this.redirectOnComplete()
            break
          }
          default:
            // console.error('unrecognised message: ' + data.call)
            break
        }
      },

      async redirectOnComplete () {
        // if there is any error shown, don't go back
        if (!(this.error !== null && this.error !== undefined)) {
          await new Promise((resolve) => setTimeout(resolve, 100))
          if (this.redirect) {
            return this.redirectToURL(this.redirect)
          } else {
            if (this.$route.query.classLearner || this.$route.query.integration) {
              this.$router.push('sessionFinished', { query: { integration: this.$route.query.integration } })
            } else {
              await this.$router.push('/admin/learner')
            }
          }
        }
      },

      async requestCode () {
        const { integration, module, classLearner } = this.$route.query

        // validate params
        if (!integration && !module && !classLearner) {
          this.error = 'Invalid query params'
          return
        }

        try {
          // This is used for LMS - if an integration param is passed
          if (integration) {
            console.log('Requesting code for LMS user...')
            const resp = await api.generateShortCodeForThirdPartyUser({
              user: this.$route.query.user,
              module: this.$route.query.module,
              lang: this.$route.query.lang,
            })
            return { shortCode: resp.shortCode, error: false }
          }

          // This is used for a user coming through the MyModules page
          if (!integration && module) {
            console.log('Requesting code for Bodyswaps user...')
            const resp = await api.getUserLoginShortCode([this.$route.query.module], this.lang)
            if (!resp) {
              // user was not logged in
              return { errorMessage: 'Not authorised to access this resource', error: true }
            }
            return { shortCode: resp.shortCode, error: false }
          }

          // This is the route for the class learner coming through an email
          if (classLearner) {
            console.log('Requesting code for Class user...')
            const resp = await api.getJoinClassShortCodeNoInvite(this.$route.query.classLearner)
            return { shortCode: resp.shortCode, error: false }
          }
        } catch (ex) {
          console.log('Code error', ex)
          return { errorMessage: ex, error: true }
        }
      },
    },
  }
</script>

<style>
#furioosPlayer {
  width: 100%;
  aspect-ratio: 16/9;
}

.stream {
  width: 100%;
  aspect-ratio: 16/9;
}

.errorMessage  {
  border: 1px solid;
  margin: 10px 0px;
  padding: 15px 10px 15px 50px;
  background-repeat: no-repeat;
  background-position: 10px center;
  color: #D8000C;
  background-color: #FFBABA;
  background-image: url('https://i.imgur.com/GnyDvKN.png');
}


</style>
